import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import {
  CAPTURE_STATUS,
  CAPTURED_CAMPAIGNS,
  CAPTURED_CONVERSATIONS,
  type CapturedCampaign,
  type CapturedConversation,
  type CaptureStatus,
  campaignLabel,
  RUN_CAPTURE,
  relativeTime,
} from "src/components/CapturedChats/helpers";
import ThreadView from "src/components/CapturedChats/ThreadView";

/** Matches the scanner's own lookback — nothing older than this is stored. */
const DEFAULT_DAYS = 45;

/**
 * Captured Chats — what members actually said back.
 *
 * The scanner has been archiving operators' 1:1 DM threads for a while, but
 * nothing showed them, so the replies sat in a table nobody could read. This is
 * the reading end: search across everything members have written, and open any
 * thread in full.
 *
 * Read-only. Replying still happens in Stream; see ThreadView for why.
 */
const CapturedChatsPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [campaign, setCampaign] = useState<string>("");
  const [selected, setSelected] = useState<CapturedConversation | null>(null);

  const { data, loading, error, refetch } = useQuery<{
    adminCapturedConversations: CapturedConversation[];
  }>(CAPTURED_CONVERSATIONS, {
    variables: { search: search || null, campaign: campaign || null, days: DEFAULT_DAYS },
    fetchPolicy: "network-only",
  });

  const { data: campaignData } = useQuery<{ adminCapturedCampaigns: CapturedCampaign[] }>(
    CAPTURED_CAMPAIGNS,
    { variables: { days: DEFAULT_DAYS }, fetchPolicy: "network-only" },
  );

  const { data: statusData, refetch: refetchStatus } = useQuery<{
    adminMessageCaptureStatus: CaptureStatus;
  }>(CAPTURE_STATUS, { fetchPolicy: "network-only" });

  const [runCapture, { loading: scanning }] = useMutation(RUN_CAPTURE);

  const conversations = data?.adminCapturedConversations ?? [];
  const status = statusData?.adminMessageCaptureStatus;
  const campaigns = campaignData?.adminCapturedCampaigns ?? [];

  const handleScan = async (): Promise<void> => {
    await runCapture();
    await Promise.all([refetch(), refetchStatus()]);
  };

  const label = (conversation: CapturedConversation): string =>
    conversation.memberName || conversation.memberEmail || conversation.memberId;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        💬 Captured Chats
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Every 1:1 DM between an operator and a member, archived from Stream so replies can be read
        and searched. Read-only — reply in Stream itself. Nothing older than {DEFAULT_DAYS} days is
        kept.
      </Alert>

      {status && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              <strong>{status.threads}</strong> threads
            </Typography>
            <Typography variant="body2">
              <strong>{status.messages}</strong> messages
            </Typography>
            <Typography variant="body2">
              <strong>{status.replies}</strong> from members
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {status.lastScanAt ? `last scan ${relativeTime(status.lastScanAt)}` : "no scan yet"}
            </Typography>
            <Box flexGrow={1} />
            <Button size="small" onClick={handleScan} disabled={scanning || status.running}>
              {scanning || status.running ? "Scanning…" : "↻ Scan now"}
            </Button>
          </Stack>
        </Paper>
      )}

      <TextField
        select
        size="small"
        label="Campaign"
        value={campaign}
        onChange={(event) => {
          setCampaign(event.target.value);
          setSelected(null);
        }}
        sx={{ mb: 2, minWidth: 280 }}
        helperText="Which outreach the messages belong to — replies included."
      >
        <MenuItem value="">All messages</MenuItem>
        {campaigns.map((item) => (
          <MenuItem key={item.campaign} value={item.campaign}>
            {campaignLabel(item.campaign)} — {item.replyCount} repl
            {item.replyCount === 1 ? "y" : "ies"} of {item.messageCount}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        size="small"
        placeholder="Search what members wrote — try “cancel”, “refund”, “not sure”"
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            setSearch(searchInput);
          }
        }}
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Button size="small" onClick={() => setSearch(searchInput)}>
                  Search
                </Button>
              </InputAdornment>
            ),
          },
        }}
        helperText={
          search ? `Showing threads containing “${search}”` : "Press Enter to search all messages"
        }
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ maxHeight: "60vh", overflow: "auto" }}>
            {loading && (
              <Box p={2}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* A failed query used to render as "Nothing captured yet", which reads
                as an empty archive rather than a broken one. Say which it is. */}
            {error && (
              <Box p={2}>
                <Alert severity="error">Couldn't load the conversations: {error.message}</Alert>
              </Box>
            )}

            {!loading && !error && conversations.length === 0 && (
              <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                  {search ? "No threads match that search." : "Nothing captured yet."}
                </Typography>
              </Box>
            )}

            <List disablePadding>
              {conversations.map((conversation) => (
                <ListItemButton
                  key={conversation.memberId}
                  selected={selected?.memberId === conversation.memberId}
                  onClick={() => setSelected(conversation)}
                  sx={{ display: "block" }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
                      {label(conversation)}
                    </Typography>
                    {/* She spoke last — nobody has answered her yet. */}
                    {!conversation.lastFromOperator && (
                      <Chip label="she replied" size="small" color="success" />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {relativeTime(conversation.lastMessageAt)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {conversation.lastFromOperator ? "us: " : "her: "}
                    {conversation.lastText}
                  </Typography>

                  {/* What this conversation came out of — the thing Stream can't tell you. */}
                  {conversation.campaigns.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ mt: 0.5 }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {conversation.campaigns.map((tag) => (
                        <Chip
                          key={tag}
                          label={campaignLabel(tag)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Box sx={{ height: "60vh" }}>
            {selected ? (
              <ThreadView
                memberId={selected.memberId}
                memberLabel={label(selected)}
                days={DEFAULT_DAYS}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Pick a conversation to read it.
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CapturedChatsPage;
